import { useState, useCallback } from 'react';
import { toast } from '@/hooks/use-toast'; // ✅ FIX: Import toast hook for use in React hook context
import { isIOS, isAndroid } from '@/utils/platform';
import i18n from '@/i18n/config';
import {
  enhancedOneSignalLogin,
  enhancedOneSignalLogout,
  requestNotificationPermissionWithPrompt,
  OneSignalAuthResult,
  OneSignalPermissionRequest
} from '@/integrations/onesignal/enhanced-auth-flow';

export interface UseOneSignalAuthOptions {
  requestPermissionImmediately?: boolean;
  customPermissionRequest?: OneSignalPermissionRequest;
  silentMode?: boolean;
  showSuccessToast?: boolean;
}

export interface UseOneSignalAuthReturn {
  // State
  isLoading: boolean;
  lastResult: OneSignalAuthResult | null;
  
  // Actions
  login: (userId: string, options?: UseOneSignalAuthOptions) => Promise<OneSignalAuthResult>;
  logout: (options?: { silentMode?: boolean; timeout?: number }) => Promise<{ success: boolean; message: string }>;
  requestPermission: (customRequest?: OneSignalPermissionRequest) => Promise<boolean>;
  
  // Utility
  getPermissionStatus: () => Promise<boolean>;
  retrySetup: (userId: string) => Promise<OneSignalAuthResult>;
  
  // Platform-specific utilities
  getPlatformInfo: () => { platform: 'ios' | 'android' | 'other'; requiresExplicitPermission: boolean };
  getPermissionInstructions: () => { title: string; description: string; actionText: string };
}

/**
 * React hook for OneSignal authentication flow
 * Provides easy-to-use methods for login, logout, and permission management
 * Now includes platform-specific handling for iOS vs Android
 */
export const useOneSignalAuth = (defaultOptions?: UseOneSignalAuthOptions): UseOneSignalAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState<OneSignalAuthResult | null>(null);

  // Platform detection utilities
  const getPlatformInfo = useCallback(() => {
    if (isIOS()) {
      return {
        platform: 'ios' as const,
        requiresExplicitPermission: true
      };
    } else if (isAndroid()) {
      return {
        platform: 'android' as const,
        requiresExplicitPermission: true // Android 13+ requires explicit permission
      };
    } else {
      return {
        platform: 'other' as const,
        requiresExplicitPermission: false
      };
    }
  }, []);

  // Get platform-specific instructions for notifications
  const getPermissionInstructions = useCallback(() => {
    const { platform } = getPlatformInfo();
    
    if (platform === 'ios') {
      return {
        title: i18n.t('notifications_ui.permission_title'),
        description: i18n.t('notifications_ui.permission_message'),
        actionText: i18n.t('notifications_ui.enable_button')
      };
    } else if (platform === 'android') {
      return {
        title: i18n.t('notifications_ui.permission_title'),
        description: i18n.t('notifications_ui.permission_message'),
        actionText: i18n.t('notifications_ui.enable_button')
      };
    } else {
      return {
        title: i18n.t('notifications_ui.permission_title'),
        description: i18n.t('notifications_ui.permission_message'),
        actionText: i18n.t('notifications_ui.enable_button')
      };
    }
  }, [getPlatformInfo]);

  const login = useCallback(async (
    userId: string, 
    options?: UseOneSignalAuthOptions
  ): Promise<OneSignalAuthResult> => {
    setIsLoading(true);
    try {
      const mergedOptions = { ...defaultOptions, ...options, showToast: toast }; // ✅ FIX: Pass toast hook
      const result = await enhancedOneSignalLogin(userId, mergedOptions);
      setLastResult(result);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, [defaultOptions]);

  const logout = useCallback(async (
    options?: { silentMode?: boolean; timeout?: number }
  ): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    try {
      const result = await enhancedOneSignalLogout({ ...options, showToast: toast }); // ✅ FIX: Pass toast hook
      setLastResult(null); // Clear last result on logout
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestPermission = useCallback(async (
    customRequest?: OneSignalPermissionRequest
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { platform } = getPlatformInfo();
      
      // Use platform-specific permission request if not provided
      if (!customRequest) {
        const instructions = getPermissionInstructions();
        customRequest = {
          title: instructions.title,
          message: instructions.description,
          allowButtonText: instructions.actionText,
          denyButtonText: i18n.t('notifications_ui.not_now_button')
        };
      }
      
      return await requestNotificationPermissionWithPrompt(customRequest, toast); // ✅ FIX: Pass toast hook
    } finally {
      setIsLoading(false);
    }
  }, [getPlatformInfo, getPermissionInstructions]);

  const getPermissionStatus = useCallback(async (): Promise<boolean> => {
    try {
      // Add timeout protection for permission status check
      const timeoutPromise = new Promise<boolean>((_, reject) => {
        setTimeout(() => reject(new Error('Permission status check timeout')), 8000);
      });
      
      const { getNotificationPermissionStatus } = await import('@/integrations/onesignal');
      const statusPromise = getNotificationPermissionStatus();
      
      return await Promise.race([statusPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error checking notification permission:', error);
      
      // Platform-specific fallback logic
      const { platform } = getPlatformInfo();
      
      try {
        if (platform === 'ios' || platform === 'android') {
          const { Capacitor } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
            const { LocalNotifications } = await import('@capacitor/local-notifications');
            const permissions = await LocalNotifications.checkPermissions();
            return permissions.display === 'granted';
          }
        }
      } catch (nativeError) {
        console.error('Native permission check failed:', nativeError);
      }
      
      return false;
    }
  }, [getPlatformInfo]);

  const retrySetup = useCallback(async (userId: string): Promise<OneSignalAuthResult> => {
    return login(userId, { 
      requestPermissionImmediately: false, 
      silentMode: true,
      showSuccessToast: false 
    });
  }, [login]);

  return {
    isLoading,
    lastResult,
    login,
    logout,
    requestPermission,
    getPermissionStatus,
    retrySetup,
    getPlatformInfo,
    getPermissionInstructions
  };
};

/**
 * Pre-configured hook for automatic permission requests
 */
export const useOneSignalAuthWithAutoPermission = (
  customPermissionRequest?: OneSignalPermissionRequest
): UseOneSignalAuthReturn => {
  return useOneSignalAuth({
    requestPermissionImmediately: true,
    customPermissionRequest,
    silentMode: false,
    showSuccessToast: true
  });
};

/**
 * Pre-configured hook for silent mode (no UI feedback)
 */
export const useOneSignalAuthSilent = (): UseOneSignalAuthReturn => {
  return useOneSignalAuth({
    requestPermissionImmediately: false,
    silentMode: true,
    showSuccessToast: false
  });
}; 